/**
 * Lista de países (ISO 3166-1 alpha-2) para el selector de "País" del tenant.
 * Los nombres se resuelven en runtime con Intl.DisplayNames (localizado), con
 * fallback a una lista en español si el navegador no lo soporta.
 */

/** Países frecuentes en Coofit: aparecen primero en el listado (igual criterio que CoofitV2). */
const PRIORITY_COUNTRY_CODES = [
  'AR', 'UY', 'PY', 'BO', 'BR', 'CL', 'CO', 'PE', 'EC', 'VE',
  'MX', 'CR', 'PA', 'GT', 'SV', 'HN', 'NI', 'CU', 'DO', 'PR',
  'ES', 'US', 'CA', 'PT', 'GB',
];

/** Resto de países ISO 3166-1 alpha-2 (orden alfabético por código). */
const OTHER_COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BQ', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ',
  'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CM', 'CN', 'CV', 'CW', 'CX', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DM', 'DZ',
  'EE', 'EG', 'EH', 'ER', 'ET',
  'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GD', 'GE', 'GF', 'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GU', 'GW', 'GY',
  'HK', 'HM', 'HR', 'HT', 'HU',
  'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT',
  'JE', 'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ',
  'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
  'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK', 'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MY', 'MZ',
  'NA', 'NC', 'NE', 'NF', 'NG', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ',
  'OM',
  'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PS', 'PW',
  'QA',
  'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SX', 'SY', 'SZ',
  'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ',
  'UA', 'UG', 'UM', 'UZ',
  'VA', 'VC', 'VG', 'VI', 'VN', 'VU',
  'WF', 'WS',
  'YE', 'YT',
  'ZA', 'ZM', 'ZW',
];

export const COUNTRY_CODES: string[] = [...PRIORITY_COUNTRY_CODES, ...OTHER_COUNTRY_CODES];

function flagEmoji(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...[...code].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0)));
}

function countryDisplayName(code: string, locale: string): string | undefined {
  try {
    const DisplayNames = (Intl as unknown as {
      DisplayNames?: new (locales: string[], options: { type: string }) => { of(code: string): string };
    }).DisplayNames;
    if (!DisplayNames) return undefined;
    return new DisplayNames([locale], { type: 'region' }).of(code) ?? undefined;
  } catch {
    return undefined;
  }
}

export interface CountryOption {
  value: string;
  label: string;
}

/** Opciones para un <select>/SearchableSelect de país, con banderita + nombre localizado. */
export function getCountrySelectOptions(locale = 'es'): CountryOption[] {
  return COUNTRY_CODES.map((code) => ({
    value: code,
    label: `${flagEmoji(code)} ${countryDisplayName(code, locale) ?? code}`.trim(),
  }));
}

export function getCountryLabel(code: string | null | undefined, locale = 'es'): string {
  if (!code) return '';
  const upper = code.trim().toUpperCase();
  const name = countryDisplayName(upper, locale) ?? upper;
  return `${flagEmoji(upper)} ${name}`.trim();
}
